import { useEffect, useRef, useState } from "react";
import { usePasskey } from "../../hooks/usePasskey";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../api/client";
import { useDialogA11y } from "../../hooks/useDialogA11y";

interface Props {
  onClose: () => void;
}

export function PasskeySetupModal({ onClose }: Props) {
  const { isSupported, isLoading, registerPasskey } = usePasskey();
  const [friendlyName, setFriendlyName] = useState("");
  const [step, setStep] = useState<"prompt" | "name">("prompt");
  const nameRef = useRef<HTMLInputElement>(null);

  // The step change unmounts the focused button; move focus to the field
  // that replaces it so the trap has something to hold.
  useEffect(() => {
    if (step === "name") nameRef.current?.focus();
  }, [step]);
  const queryClient = useQueryClient();

  // No `open` prop: this component renders only while the dialog should be
  // shown, and bails out below when the platform lacks passkey support.
  const dialogRef = useDialogA11y<HTMLDivElement>({ open: isSupported, onClose });

  if (!isSupported) return null;

  const handleSetup = async () => {
    try {
      await registerPasskey(friendlyName || undefined);
      queryClient.invalidateQueries({ queryKey: ["passkeys"] });
      onClose();
    } catch {
      // Error already handled by usePasskey
    }
  };

  const handleDismiss = async () => {
    try {
      await apiClient.put("/auth/profile", { passkeyPromptDismissed: true });
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    } catch {
      // Non-blocking
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="passkey-modal-title"
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8"
      >
        {step === "prompt" ? (
          <>
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-brand-lagune-100 flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-lagune-600">
                  <path d="M2 18v3c0 .6.4 1 1 1h4v-3h3v-3h2l1.4-1.4a6.5 6.5 0 1 0-4-4Z" />
                  <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
                </svg>
              </div>
              <h2 id="passkey-modal-title" className="text-xl font-bold text-slate-900">
                Connexion rapide avec Passkey
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                Configurez un passkey pour vous connecter instantanément avec
                votre empreinte, votre visage ou le code PIN de votre appareil.
              </p>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => setStep("name")}
                className="w-full rounded-xl bg-brand-lagune-700 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-lagune-800 transition-colors"
              >
                Configurer maintenant
              </button>
              <button
                onClick={handleDismiss}
                className="w-full rounded-xl border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Plus tard
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id="passkey-modal-title" className="text-lg font-bold text-slate-900 mb-4">
              Nommer votre passkey
            </h2>
            <p className="text-sm text-slate-600 mb-4">
              Donnez un nom à ce passkey pour le reconnaître plus tard (optionnel).
            </p>
            <input
              ref={nameRef}
              type="text"
              value={friendlyName}
              onChange={(e) => setFriendlyName(e.target.value)}
              placeholder="Ex: Mon iPhone, PC Bureau"
              className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-brand-lagune-600 focus:ring-1 focus:ring-ring outline-none mb-4"
              maxLength={100}
            />
            <div className="space-y-3">
              <button
                onClick={handleSetup}
                disabled={isLoading}
                className="w-full rounded-xl bg-brand-lagune-700 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-lagune-800 transition-colors disabled:opacity-50"
              >
                {isLoading ? "Configuration en cours..." : "Enregistrer le passkey"}
              </button>
              <button
                onClick={handleDismiss}
                disabled={isLoading}
                className="w-full rounded-xl border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Plus tard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
